/* ITER239 · Phase 3 · Part A — Petty Cash Category Settings (admin-only).
 *
 * Soft-deactivation, not hard delete. Renames propagate via category_id;
 * historical spend re-labels automatically. Inline edit + add + toggle.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Plus, Loader2, Check, X, Edit3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PettyCashCategorySettings() {
  const { getHeaders, user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'area_manager'].includes(user?.role || '');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [toggling, setToggling] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/petty-cash/categories?include_inactive=true`,
        { headers: getHeaders() });
      setRows(r.data || []);
    } catch (e) {
      toast.error('Could not load categories');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { reload(); }, [reload]);

  const addCategory = async () => {
    const label = newLabel.trim();
    if (!label) return toast.error('Enter a category name');
    setAdding(true);
    try {
      await axios.post(`${API}/petty-cash/categories`, { label }, { headers: getHeaders() });
      toast.success(`Added "${label}"`);
      setNewLabel('');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add category');
    } finally {
      setAdding(false);
    }
  };

  const saveRename = async (id) => {
    const label = editLabel.trim();
    if (!label) return toast.error('Enter a name');
    try {
      await axios.put(`${API}/petty-cash/categories/${id}`, { label },
        { headers: getHeaders() });
      toast.success(`Renamed to "${label}" — all historical spend re-labelled`);
      setEditId(null);
      setEditLabel('');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not rename');
    }
  };

  const toggleActive = async (row) => {
    setToggling(row.id);
    try {
      await axios.put(`${API}/petty-cash/categories/${row.id}`,
        { active: !row.active }, { headers: getHeaders() });
      toast.success(row.active
        ? 'Deactivated — hidden from the create dropdown; reporting still shows past spend'
        : 'Reactivated — now available in the create dropdown');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update');
    } finally {
      setToggling(null);
    }
  };

  const back = isAdmin ? '/admin/petty-cash/wallets' : '/manager/petty-cash/wallets';

  return (
    <div className="space-y-4" data-testid="category-settings-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to={back} className="inline-flex items-center gap-1 text-[12px] text-[#5C6B64] hover:text-[#1A2421]" data-testid="cat-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to wallets
        </Link>
        <div>
          <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-[#1A2421]">Categories</h1>
          <p className="text-[12px] text-[#5C6B64]">Editable expense taxonomy · soft-deactivation preserves history</p>
        </div>
      </div>

      {!isAdmin ? (
        <p className="text-[12px] text-[#C45A41]">Admin-only screen.</p>
      ) : (
        <>
          <Card className="border-[#E2E8DF]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#1A2421]">Add a new category</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
                  placeholder="e.g. Vendor Tasting, Festive Decor…"
                  className="h-9 max-w-sm bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
                  data-testid="new-category-input" />
                <Button onClick={addCategory} disabled={adding || !newLabel.trim()}
                  className="h-9 text-xs gap-1 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                  data-testid="add-category-btn">
                  {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Add
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8DF]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#1A2421] flex items-center justify-between">
                <span>All categories</span>
                <span className="text-[10px] text-[#5C6B64] font-normal">{rows.length} total</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="text-[12px] text-[#5C6B64]">Loading…</div>
              ) : (
                <div className="divide-y divide-[#F0F2EE]">
                  {rows.map(row => (
                    <div key={row.id}
                      className="flex items-center justify-between gap-3 py-2.5"
                      data-testid={`category-row-${row.id}`}>
                      <div className="min-w-0 flex-1">
                        {editId === row.id ? (
                          <div className="flex items-center gap-1.5">
                            <Input value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveRename(row.id); }}
                              className="h-8 max-w-xs bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
                              data-testid={`category-rename-input-${row.id}`} />
                            <Button size="sm" onClick={() => saveRename(row.id)}
                              className="h-8 text-[11px] px-2 gap-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                              data-testid={`category-rename-save-${row.id}`}>
                              <Check className="w-3 h-3" /> Save
                            </Button>
                            <Button size="sm" variant="outline"
                              onClick={() => { setEditId(null); setEditLabel(''); }}
                              className="h-8 text-[11px] px-2 gap-1 border-[#E2E8DF] text-[#5C6B64]">
                              <X className="w-3 h-3" /> Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[13px] font-semibold ${row.active ? 'text-[#1A2421]' : 'text-[#5C6B64] line-through'}`}>
                              {row.label}
                            </span>
                            {!row.active && (
                              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                                Inactive
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        {editId !== row.id && (
                          <Button size="sm" variant="ghost"
                            onClick={() => { setEditId(row.id); setEditLabel(row.label); }}
                            className="h-7 text-[11px] gap-1 text-[#5C6B64] hover:text-[#1A2421]"
                            data-testid={`category-rename-btn-${row.id}`}>
                            <Edit3 className="w-3 h-3" /> Rename
                          </Button>
                        )}
                        <div className="flex items-center gap-1.5">
                          <Label className="text-[10px] text-[#5C6B64]">Active</Label>
                          <Switch
                            checked={!!row.active}
                            disabled={toggling === row.id}
                            onCheckedChange={() => toggleActive(row)}
                            data-testid={`category-toggle-${row.id}`} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-[10px] text-[#5C6B64] leading-snug">
            Deactivating removes a category from the voucher dropdown but historical spend stays under that name in reporting.
            Renaming re-labels every historical voucher via the category id — no audit gap.
          </p>
        </>
      )}
    </div>
  );
}
